<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                title="Task Management"
                :showAddNew="true"
                :showExtraBtn="true"
                extraBtnTitle="Calendar View"
                btnTitle="Add New"
                @onClickExtraBtn="navigateToCalendarPage"
                @onClickAddNewButton="onClickAddNew"
            />
        </div>
        <div class="my-2 px-2">
            <DateQuerySetter
                btnTitle="Go"
                @onClickGo="onClickToGo"
                class="tds-vds-query-setter"
            >
              <div class="col-12 col-sm-6 col-md-4 col-lg-3" v-if="isUserAccess">
                <label for="colFormLabel" class="col-form-label">Team Member</label>
                <AsyncSelect
                    placeholder="Team Member"
                    v-model="contactProfile"
                    :api-service="fetchContactProfiles"
                    :additional-query="{roles: 'employee', status: 'active'}"
                    label="name"
                />
              </div>
              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Activity Group</label>
                <AsyncSelect
                    placeholder="Select Activity"
                    v-model="activityGroup"
                    :additional-query="{type: 'group'}"
                    :api-service="fetchActivitySetupEndpoint"
                    label="name"
                    @option:selected="onChangeActivityGroup"
                />
              </div>

              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Organization Activity</label>
                <AsyncSelect
                    placeholder="Select Activity"
                    v-model="organizationActivity"
                    :api-service="fetchActivitySetupEndpoint"
                    :additional-query="{ group_id: activityGroup && activityGroup.id }"
                    label="name"
                    ref="organizationActivityDropdown"
                />
              </div>

              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Client</label>
                <AsyncSelect
                    placeholder="Client"
                    v-model="client"
                    :api-service="fetchContactProfiles"
                    :additional-query="{roles: 'customer,lead'}"
                    label="name"
                />
              </div>
              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Project</label>
                <v-select
                    placeholder="Select Project"
                    v-model="project"
                    :options="projects"
                    label="name"
                />
              </div>
              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Task Role</label>
                <v-select
                    placeholder="Select Task Role"
                    :options="taskCalendarFilterList"
                    v-model="taskCalendarType"
                    label="name"
                    :reduce="name => name.value"
                />
              </div>
              <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label for="colFormLabel" class="col-form-label">Task Status</label>
                <v-select
                    placeholder="Select Task Status"
                    :options="taskCalendarStatusList"
                    v-model="taskCalendarStatus"
                    label="name"
                    :reduce="name => name.value"
                />
              </div>
            </DateQuerySetter>
        </div>

        <div class="col-12 px-2 mt-2 mb-4">
          <div class="card p-2" style="min-height: 200px">
            <div class="table-responsive">
              <table class="table">
                <thead>
                <tr>
                  <th>Task Title</th>
                  <th>Full Description</th>
                  <th>Assigned To</th>
                  <th>Report To</th>
                  <th>Created By</th>
                  <th>Organizations Activity</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Project Activity</th>
                  <th>Participants</th>
                  <th>Start Date & Time</th>
                  <th>End Date & Time</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Attachment</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in tableItems" :key="i"
                    :style="{
                    backgroundColor: taskStatusColors[item.status].bg || '#f9fafb',
                    color: taskStatusColors[item.status].text || '#111827',
                  }"
                >
                  <td @click="goToEdit(item.id)" class="text-decoration-underline cursor-pointer text-primary">{{ item.short_description }}</td>
                  <td>{{ item.full_description }}</td>
                  <td>{{ item.assigned_to }}</td>
                  <td>{{ item.report_to }}</td>
                  <td>{{ item.user_name }}</td>
                  <td>{{ item.activity_setup_id }}</td>
                  <td>{{ item.client_id }}</td>
                  <td>{{ item.project_id }}</td>
                  <td>{{ item.project_activity_id }}</td>
                  <td>{{ item.participant_id }}</td>
                  <td>{{ item.format_start_time }}</td>
                  <td>{{ item.format_end_time }}</td>
                  <td>{{ item.type }}</td>
                  <td>{{ item.status }}</td>
                  <td>
                    <button v-if="item.attachments.length > 0" class="btn btn-primary btn-sm"
                            style="cursor: pointer"
                            @click="viewAttachments(item.attachments)"
                    >
                      <i class="fas fa-eye"></i>
                      View
                    </button>
                    <span v-else class="badge btn-danger">
                            No attachments
                          </span>
                  </td>
                </tr>
                </tbody>
              </table>
              <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
            </div>
          </div>
        </div>

        <div class="px-2 position-absolute bottom-0 mt-4">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

        <TaskEventScheduler
            v-if="store.state.isModalOpen"
            :startDate="startDate"
            :endDate="endDate"
            :editData="singleActivity"
            :taskStatus="taskStatus"
            :workStatus="workStatus"
            :markAsDoneShow="markAsDoneShow"
            :isCompleted="isCompleted"
            @submitted="onEventSubmitted"
        />

        <ViewAttachment
            ref="viewAttachmentRef"
            :attachments="modalAttachments"
        />
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import handleActivity from "@/services/modules/procurement/activity";
import { computed } from 'vue';
import handleChartAccount from '@/services/modules/chartAccount'
import handleContact from '@/services/modules/contact'
import handleDropDowns from '@/services/modules/dropdowns'
import handleProjects from "@/services/modules/procurement/project";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import TaskEventScheduler from '@/components/molecule/company/admin/TaskEventScheduler.vue';
import { formatDateTime } from '@/services/utils/global.js';
import ViewAttachment from '@/views/page/company/ngo/voucher/payment/ViewAttachmentModal.vue'
import { taskStatusColors } from '@/services/utils/global.js';

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const taskCalendarFilterList = ref([]);
const projects = ref([]);
const taskCalendarStatusList = ref([]);
const taskCalendarType = ref('');
const taskCalendarStatus = ref('');
const activityGroup = ref('');
const organizationActivity = ref('');
const client = ref('');
const contactProfile = ref('');
const project = ref('');
const loading = ref(false)
const isUserAccess = ref(false)
const tableItems = ref([]);
const params = ref({
    offset: 20,
});
const singleActivity = ref(null);
const taskStatus = ref({});
const workStatus = ref({});
const markAsDoneShow = ref(false);
const isCompleted = ref(false);
const startDate = ref('');
const endDate = ref('');
const modalAttachments = ref([]);
const viewAttachmentRef = ref();
const organizationActivityDropdown = ref(null)

const token = computed(() => localStorage.getItem('token'));

const { fetchTaskManagementList, fetchSingleTaskActivityLog } = handleActivity()
const { fetchHome } = handleChartAccount()
const { fetchContactProfiles } = handleContact()
const { fetchActivitySetupEndpoint } = handleDropDowns();
const { fetchProjects } = handleProjects()

const onClickToGo = () => {
   getTaskManagementList();
}

const onChangeActivityGroup = () => {
  organizationActivity.value = null;
  organizationActivityDropdown.value.clear();
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
};

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

function navigateToCalendarPage() {
  router.push({name: `task-calendar`, params: route.params, query: route.query});
}

function onClickAddNew() {
  const selectedDate = new Date();
  const now = new Date();

  selectedDate.setHours(now.getHours());
  selectedDate.setMinutes(now.getMinutes());

  const selectedEnd = new Date(selectedDate);
  selectedEnd.setHours(selectedDate.getHours() + 1);

  startDate.value = formatDateTime(selectedDate);
  endDate.value = formatDateTime(selectedEnd);
  singleActivity.value = null;
  taskStatus.value = {};
  workStatus.value = {};
  markAsDoneShow.value = false;
  isCompleted.value = false;
  store.state.isModalOpen = true;
}

const goToEdit = async (activityId) =>  {
  if (!activityId) return;
  markAsDoneShow.value = false;
  isCompleted.value = false;
  loading.value = true;
  singleActivity.value = null;
  taskStatus.value = {};
  workStatus.value = {};

  try {
    const res = await fetchSingleTaskActivityLog(activityId, getQuery());
    if (!res.status) return showError(res.message);
    let response = res.data;
    singleActivity.value = response;
    taskStatus.value = res.task_status;
    if (res.work_status){
      workStatus.value = res.work_status;
    }
    markAsDoneShow.value = true;
    if (['completed', 'approved'].includes(response.status)) {
      isCompleted.value = true;
    }
    store.state.isModalOpen = true;
  } finally {
    loading.value = false;
  }
}

const onEventSubmitted = () => {
   getTaskManagementList();
};

const viewAttachments = (attachments) => {
  modalAttachments.value = [];
  modalAttachments.value = attachments;
  viewAttachmentRef.value && viewAttachmentRef.value.toggleModal();
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getTaskManagementList();
}

const getTaskManagementList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchTaskManagementList(query).then(res => {
        if (!res.status){
            store.commit("resetPagination")
            return
        }
        tableItems.value = res.data.data;
        isUserAccess.value = res.user_filter_access
        setPagination(res.data)
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}&route_name=task-calendar`;
    if (route.query.page) query += '&page=' + route.query.page;
    if (route.query.start) query += '&start_date=' + route.query.start;
    if (route.query.end) query += '&end_date=' + route.query.end;
    if (taskCalendarType.value) query += `&task_calendar_type=${taskCalendarType.value}`;
    if (project.value) query += `&project=${project.value.id}`;
    if (organizationActivity.value) query += `&organization_activity=${organizationActivity.value.id}`;
    if (activityGroup.value) query += `&activity_group=${activityGroup.value.id}`;
    if (client.value) query += `&client=${client.value.id}`;
    if (contactProfile.value) query += `&contact_profile_id=${contactProfile.value.id}`;
    if (taskCalendarStatus.value) query += `&status=${taskCalendarStatus.value}`;
    query += '&offset=' + params.value.offset
    return query;
}

onMounted(() => {
    let companyQuery = '?company_id=' + route.params.companyId;
    let projectQuery = companyQuery + '&with_donors=1';

    Promise.all([
        getTaskManagementList(),
        fetchHome().then(res => {
          if (!res.data) {
            return;
          }
          if (res.data && res.data.task_calendar_filter){
            taskCalendarFilterList.value = res.data.task_calendar_filter;
          }
          if (res.data && res.data.task_calendar_status){
            taskCalendarStatusList.value = res.data.task_calendar_status;
          }
        }),
        fetchProjects(projectQuery).then(res => {
          if (res.data) {
            projects.value = res.data
          }
        })
    ])
})

</script>
